import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, switchMap, takeWhile, timer } from 'rxjs';

import { environment } from '../../../../environments/environment';

import {
  FinanzcheckReminderOverview,
  FinanzcheckReminderRequest,
  FinanzcheckReminderResult,
  PortfolioAnalysis,
  PortfolioClientInsight,
  PortfolioClientSort,
  PortfolioMessagePreview,
  PortfolioMessagePreviewRequest,
  PortfolioMessageRequest,
  PortfolioMessageResult,
  PortfolioPreview,
  PortfolioRequest,
} from '../../model';

/** HTTP client of the Portfolio Analysis (Bestandsanalyse) endpoints of the `ai` service. */
@Injectable({ providedIn: 'root' })
export class PortfolioAnalysisService {
  private readonly base: string = `${environment.apiEndpoint}/ai/portfolio`;

  constructor(private readonly http: HttpClient) {}

  /** Own scan, or with `advisorId` the scan of an advisor of the company admin's structure. */
  private scanBase(advisorId?: string | null): string {
    return advisorId ? `${this.base}/advisors/${advisorId}` : this.base;
  }

  /** Free dry run: how many clients match the scope and what the scan would cost. */
  public preview(request: PortfolioRequest): Observable<PortfolioPreview> {
    return this.http.post<PortfolioPreview>(`${this.base}/preview`, request);
  }

  /** Starts a scan; answers 202 with the RUNNING handle (or the scan already in flight). */
  public start(request: PortfolioRequest): Observable<PortfolioAnalysis> {
    return this.http.post<PortfolioAnalysis>(this.base, request);
  }

  /**
   * The advisor's current scan (running or finished); 404 if none. With `advisorId` a company admin reads the scan of
   * an advisor of their structure (read-only, Struktur → Berater).
   */
  public getLatest(advisorId?: string | null): Observable<PortfolioAnalysis> {
    return this.http.get<PortfolioAnalysis>(`${this.scanBase(advisorId)}/latest`);
  }

  /** Free cost estimate of a scan over all clients of an advisor of the admin's structure, against the advisor's wallet. */
  public previewForAdvisor(advisorId: string): Observable<PortfolioPreview> {
    return this.http.get<PortfolioPreview>(`${this.scanBase(advisorId)}/preview`);
  }

  /** A company admin starts the scan of an advisor of their structure; charged to the advisor (202, RUNNING handle). */
  public startForAdvisor(advisorId: string): Observable<PortfolioAnalysis> {
    return this.http.post<PortfolioAnalysis>(this.scanBase(advisorId), {});
  }

  public get(portfolioUid: string): Observable<PortfolioAnalysis> {
    return this.http.get<PortfolioAnalysis>(`${this.base}/${portfolioUid}`);
  }

  public getClients(
    portfolioUid: string,
    sort: PortfolioClientSort = 'PRIORITY',
    inScope?: boolean,
    limit?: number,
    advisorId?: string | null,
  ): Observable<PortfolioClientInsight[]> {
    let params: HttpParams = new HttpParams().set('sort', sort);
    if (inScope !== undefined && inScope !== null) {
      params = params.set('in_scope', inScope);
    }
    if (limit) {
      params = params.set('limit', limit);
    }
    return this.http.get<PortfolioClientInsight[]>(`${this.scanBase(advisorId)}/${portfolioUid}/clients`, { params });
  }

  public getClient(portfolioUid: string, clientId: string): Observable<PortfolioClientInsight> {
    return this.http.get<PortfolioClientInsight>(`${this.base}/${portfolioUid}/clients/${clientId}`);
  }

  /** The "Alle Kunden" table as CSV (UTF-8 BOM, `;` separator) for Excel. */
  public exportCsv(portfolioUid: string): Observable<Blob> {
    return this.http.get(`${this.base}/${portfolioUid}/export.csv`, { responseType: 'blob' });
  }

  public cancel(portfolioUid: string): Observable<PortfolioAnalysis> {
    return this.http.post<PortfolioAnalysis>(`${this.base}/${portfolioUid}/cancel`, null);
  }

  public delete(portfolioUid: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${portfolioUid}`);
  }

  /** Clients without a Finanzcheck of the current scan, the default message and the conversion counter. */
  public getFinanzcheckReminderOverview(): Observable<FinanzcheckReminderOverview> {
    return this.http.get<FinanzcheckReminderOverview>(`${this.base}/reminders/finanzcheck`);
  }

  /** Sends the Finanzcheck reminder as chat messages; answers 202, delivery reports over WebSocket. */
  public sendFinanzcheckReminder(request: FinanzcheckReminderRequest): Observable<FinanzcheckReminderResult> {
    return this.http.post<FinanzcheckReminderResult>(`${this.base}/reminders/finanzcheck`, request);
  }

  /** Composer state of a segment message: candidates with reachability/cooldown and the product text. */
  public previewMessage(request: PortfolioMessagePreviewRequest): Observable<PortfolioMessagePreview> {
    return this.http.post<PortfolioMessagePreview>(`${this.base}/messages/preview`, request);
  }

  /** Sends a segment message as chat messages; answers 202, delivery reports over WebSocket. */
  public sendMessage(request: PortfolioMessageRequest): Observable<PortfolioMessageResult> {
    return this.http.post<PortfolioMessageResult>(`${this.base}/messages`, request);
  }

  /** Polls the current scan until it leaves RUNNING/PENDING — fallback when the WebSocket is quiet. */
  public pollLatest(intervalMs: number = 10000, advisorId?: string | null): Observable<PortfolioAnalysis> {
    return timer(intervalMs, intervalMs).pipe(
      switchMap(() => this.getLatest(advisorId)),
      takeWhile((scan: PortfolioAnalysis): boolean => scan.status === 'RUNNING' || scan.status === 'PENDING', true),
    );
  }
}
